import { AfterViewInit, Component, inject, Input as RouterInput, ViewChild } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { Employee } from '../../employee/shared/employee.model';
import { BaseComponent } from '../../shared/base.component';
import { DateUtil } from '../../shared/date_util';
import { TransferDroneState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { CustomerNamesComponent } from '../../ui-components/customer-names/customer-names.component';
import { DealerNamesComponent } from '../../ui-components/dealer-names/dealer-names.component';
import { DroneService } from '../shared/drone.service';

@Component({
    selector: 'app-transfer-drone',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        SiteHeaderComponent,
        DealerNamesComponent,
        CustomerNamesComponent
    ],
    templateUrl: './transfer-drone.component.html',
    styleUrl: './transfer-drone.component.css'
})
export class TransferDroneComponent extends BaseComponent implements AfterViewInit {

    @RouterInput() id: number = -1;
    @ViewChild(DealerNamesComponent) dealerNamesComponent!:DealerNamesComponent;
    @ViewChild(CustomerNamesComponent) customerNamesComponent!:CustomerNamesComponent;

    private droneService: DroneService = inject(DroneService);
    private state: TransferDroneState | undefined;

    private static _transferringDrone = "Transferring Drone";
    private static _transferringDroneError = `${TransferDroneComponent._transferringDrone} error:`;

    protected transferDroneForm = new FormGroup({
        serialNumber: new FormControl({ value: "", disabled: true }),
        model: new FormControl({ value: "", disabled: true }),
        manufactureDate: new FormControl({ value: "", disabled: true }),
        transferredToDealerDate: new FormControl({ value: "", disabled: true }),
        transferDate: new FormControl(new Date()),
    });

    private _minDate: Date | undefined;
    private _maxDate: Date;
    private _notYetTransferred: boolean = true;

    public constructor() {
        super();
        this._maxDate = new Date();
        this.transferDroneForm.controls.transferDate.setValue(this._maxDate);
    }

    public ngOnInit() {
        this.state = this.stateManager.transferDroneState;

        if (this.state != undefined) {
            this.id = this.state.id;
            this.transferDroneForm = this.state.transferDroneForm;
            this._notYetTransferred = this.state.notYetTransferred;
            super.restoreState(this.state.baseComponentState);
        } else {
            const drones = this.stateManager.viewDronesState!.drones
            const drone = drones.find(d => d.id == this.id)!;
            this.transferDroneForm.controls.serialNumber.setValue(drone.serial_number);
            this.transferDroneForm.controls.model.setValue(drone.model);
            this.transferDroneForm.controls.manufactureDate.setValue(drone.manufacture_date.toLocaleDateString());

            // Minimum Date for Transfer Date
            //
            //   Transfer to Dealer
            // 
            //     To enforce good business practices within the AeroStar Dynamics
            //     organization, the minimum transfer date is either the Manufacture Date
            //     plus 1 day or current date minus 6 days, whichever is closest to
            //     current date.
            //
            //   Transfer to Customer
            //
            //     - If an AeroStar Dymamics employee is performing the transfer to
            //       Customer, then minimum transfer date is either the date of transfer
            //       to the dealer or current date minus 6 days, whichever is closest
            //       to current date.
            //
            //     - If a Dealer is performing the transfer to Customer, then minimum
            //       transfer date is the date of transfer to the Dealer.
            //
            // The 6 days from current date requirement for AeroStar Dynamics employees
            // is to enforce good business practices within the AeroStar Dynamics
            // organization. The business rules for Dealers performing transfer to
            // Customer are more lenient as we do not want to discourage dealers from
            // recording the transfer to Customer.

            let minDate = DateUtil.adjustDate(this._maxDate, -6);
            
            if (drone.drone_transfers.length == 0) {
                const manufactureDate = DateUtil.adjustDate(drone.manufacture_date, 1)
                if (manufactureDate >= minDate) {
                    minDate = manufactureDate;
                }
            } else  {
                const transferToDealer = drone.drone_transfers[drone.drone_transfers.length - 1];
                const transferredToDealerDate = transferToDealer.transferred_date;
                this.transferDroneForm.controls.transferredToDealerDate.setValue(transferredToDealerDate.toLocaleDateString());

                if (this.userType === "employee") {
                    if (transferredToDealerDate > minDate) {
                        minDate = transferredToDealerDate;
                    }
                } else {
                    minDate = transferredToDealerDate;
                }
            }

            this._minDate = minDate;
            this._notYetTransferred = drone.drone_transfers.length == 0;
        }
    }

    public ngAfterViewInit() {
        if (this.state != undefined) {
            if (this._notYetTransferred) {
                this.dealerNamesComponent.restoreState(this.state.dealerNamesComponentState!);
            } else {
                this.customerNamesComponent.restoreState(this.state.customerNamesComponentState);
            }
        } else {
            if (this._notYetTransferred) {
                this.dealerNamesComponent.init("transfer-drone", undefined, false);
            } else {
                let dealerId;
                if (this.userType === "employee") {
                    const userAccount: Employee = this.userAccount as Employee;
                    dealerId = userAccount.dealer_id;
                }
                this.customerNamesComponent.init("transfer-drone", dealerId, false);
            }
        }
    }

    public ngOnDestroy() {
        const state: TransferDroneState = {} as TransferDroneState;
        state.id = this.id;
        state.transferDroneForm = this.transferDroneForm;
        state.notYetTransferred = this._notYetTransferred;

        if (this._notYetTransferred) {
            state.dealerNamesComponentState = this.dealerNamesComponent.getState();
        } else {
            state.customerNamesComponentState = this.customerNamesComponent.getState();
        }

        state.baseComponentState = super.getState();
        this.stateManager.transferDroneState = state;
    }

    protected cancel(): void {
        this.router.navigate(["/view-drones"], { state: { restore: true } });
    }

    protected async save() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            let transferToType: number = -1;
            let transferToId: number = -1;

            if (this._notYetTransferred) {
                transferToType = 2;
                transferToId = this.dealerNamesComponent.selectedDealer;
            } else {
                transferToType = 4;
                transferToId = this.customerNamesComponent.selectedCustomer;
            }
    
            const observable = await this.droneService.transferDrone(await getIdToken(currentUser),
                                this.id, transferToType, transferToId, this.transferDroneForm.controls.transferDate.value?.toISOString() || '');
            super.showSnackBar(`${TransferDroneComponent._transferringDrone}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["transfer-drone"] == "success") {
                        const queryParam = `Drone transferred.`;
                        this.router.navigate(["/view-drones"], { queryParams: { "overrideSnackBar": queryParam }, state: { refresh: true } });
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${TransferDroneComponent._transferringDroneError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
                }
            })
        }
    }

    protected get notYetTransferred(): boolean {
        return this._notYetTransferred;
    }


    protected get minDate(): Date {
        return this._minDate!;
    }
    protected get maxDate(): Date {
        return this._maxDate;
    }
}
